import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tooltip,
    Typography
} from '@mui/material';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { connect, useDispatch } from 'react-redux';
import { If } from '../../common/IfComponent';
import { RounderDto } from '../../common/PricePrgClient';
import { ProgressButton } from '../../common/ProgressButton';
import { StrictModeDroppable } from '../../common/StrictModeDroppable';
import { RootState } from '../../store';
import { showAlertDialog } from '../ui/redux/uiSlices';
import CostGroupDraggable from './CostGroupDraggable';
import { CostTypeList } from './CostTypeList';
import ExecuteCalculation from './ExecuteCalculation';
import { MarkupCalculator } from './MarkupCalculator';
import CreateCostDialog from './dialogs/CreateCostDialog';
import EditCostDialog from './dialogs/EditCostDialog';
import EditCostGroupDialog from './dialogs/EditCostGroupDialog';
import RecalculatePricesDialog from './dialogs/RecalculatePricesDialog';
import {
    reorderCost,
    showNewCostDialog,
    showNewCostGroupDialog,
    showRecalculateDialog,
    updateCalculation
} from './redux/calculationSlices';

const mapStateToProps = (state: RootState) => ({
    costtypes: state.calculation.costtypes,
    calculation: state.calculation.calculation,
    isEditCostDialogVisible: state.calculation.isEditCostDialogVisible,
    rounders: state.calculation.rounders,
    isRecalculatingPrices: state.calculation.isRecalculatingPrices
});

type Props = ReturnType<typeof mapStateToProps>;

function CalculationCanvas({ costtypes, calculation, rounders, isRecalculatingPrices }: Props) {
    const dispatch = useDispatch();

    const dispatchCostReorder = (costId: number, destIndex: number) => {
        dispatch(reorderCost.request({ costId, order: destIndex }));
    };

    const handleNewCost = (costTypeId: number, costGroupId: number) => {
        dispatch(
            showNewCostDialog({
                costGroupId,
                costTypeId,
                showDialog: true
            })
        );
    };

    const onDragEnd = (result: DropResult) => {
        // dropped outside the list

        if (!result.destination) {
            return;
        }
        // const sourceIndex = result.source.index;
        const destIndex = result.destination.index;
        // The cost is dropped inside the same cost group
        if (result.type === 'droppableCost' && result.source.droppableId === result.destination.droppableId) {
            const costId = result.draggableId.substring(result.draggableId.lastIndexOf('_') + 1);
            if (result.destination.droppableId !== 'CostTypesList') {
                dispatchCostReorder(parseInt(costId, undefined), destIndex);
            }
        } else if (
            result.type === 'droppableCost' &&
            result.source.droppableId === 'CostTypesList' &&
            result.destination.droppableId.includes('costGroup')
        ) {
            const costGroupId = Number(
                result.destination.droppableId.substring(result.destination.droppableId.lastIndexOf('_') + 1)
            );
            const currentCostGroup = calculation.costGroups.find(c => c.costGroupId === costGroupId);
            if (currentCostGroup?.inheritedFrom != null) {
                dispatch(
                    showAlertDialog({
                        dialogMessage: 'You cannot add a cost on an inherited cost group.',
                        dialogTitle: 'Warning!',
                        icon: 'warning'
                    })
                );
                return;
            } else if (
                currentCostGroup?.costGroupType !== null &&
                currentCostGroup?.costGroupType === 1 &&
                result.source.index !== 0
            ) {
                dispatch(
                    showAlertDialog({
                        dialogMessage:
                            'You cannot only add percentage costs on a grouped percentage cost group.',
                        dialogTitle: 'Warning!',
                        icon: 'warning'
                    })
                );
                return;
            }
            handleNewCost(result.source.index, costGroupId);
        }
    };

    const handleRounderChanged = (value: unknown) => {
        dispatch(
            updateCalculation.request({
                calculationId: calculation.calculationId,
                name: calculation.name,
                rounder: value as string
            })
        );
    };

    const dispatchShowNewCostGroupDialog = () => {
        dispatch(showNewCostGroupDialog());
    };

    const handleRecalculatePrices = () => {
        dispatch(showRecalculateDialog(true));
    };

    const getToolTip = (rounder: RounderDto) => {
        return (
            <Tooltip title={rounder.tooltipText} style={{ marginLeft: 'auto' }}>
                <InfoIcon
                    fill="false"
                    style={{
                        color: 'grey',
                        marginLeft: 'auto',
                        fontSize: '18',
                        verticalAlign: 'bottom'
                    }}
                />
            </Tooltip>
        );
    };

    return (
        <div>
            <Grid container spacing={4}>
                <Grid item xs={8}>
                    <Paper>
                        <Grid container direction="row" justifyContent="space-between">
                            <Grid item>
                                <Grid container direction="row" spacing={4}>
                                    <Grid item>
                                        <Box
                                            component="div"
                                            sx={{
                                                width: '100%',
                                                marginTop: '15px',
                                                marginLeft: '34px',
                                                marginBottom: '15px'
                                            }}
                                        >
                                            <Button
                                                color="primary"
                                                onClick={dispatchShowNewCostGroupDialog}
                                                component="span"
                                                variant="contained"
                                            >
                                                Add cost group
                                            </Button>
                                        </Box>
                                    </Grid>
                                    <Grid item>
                                        <FormControl variant="standard">
                                            <InputLabel id="rounder-label">Rounder</InputLabel>
                                            <Select
                                                label="rounder"
                                                labelId={'rounder-label'}
                                                onChange={event => {
                                                    handleRounderChanged(event.target.value);
                                                }}
                                                readOnly={calculation.inheritedFrom != null}
                                                value={calculation.rounder ?? 'NoRoundingRounder'}
                                            >
                                                {rounders.map(rounder => {
                                                    return (
                                                        <MenuItem
                                                            key={rounder.rounderName}
                                                            value={rounder.rounderName}
                                                        >
                                                            {rounder.displayName}

                                                            <If
                                                                condition={rounder.tooltipText != null}
                                                                then={getToolTip(rounder)}
                                                                else={null}
                                                            />
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Box
                                    component="div"
                                    sx={{ marginTop: '15px', marginRight: '16px', marginBottom: '15px' }}
                                >
                                    <ProgressButton
                                        color="primary"
                                        onClick={handleRecalculatePrices}
                                        variant="contained"
                                        isLoading={isRecalculatingPrices}
                                    >
                                        Recalculate prices
                                    </ProgressButton>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid container direction="row">
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Grid container spacing={4}>
                                    <Grid item xs={8}>
                                        <StrictModeDroppable droppableId="calculation" type="costGroup">
                                            {providedDroppable => (
                                                <Box
                                                    component="div"
                                                    sx={{ width: '500px', padding: '8px' }}
                                                    ref={providedDroppable.innerRef}
                                                    {...providedDroppable.droppableProps}
                                                >
                                                    {calculation.costGroups.map((item, index) => (
                                                        <CostGroupDraggable
                                                            key={item.costGroupId}
                                                            costGroup={item}
                                                            index={index}
                                                        />
                                                    ))}
                                                    {providedDroppable.placeholder}
                                                </Box>
                                            )}
                                        </StrictModeDroppable>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <CostTypeList costTypes={costtypes} />
                                    </Grid>
                                </Grid>
                            </DragDropContext>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item xs={4}>
                    <Paper>
                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Typography sx={theme => ({ fontSize: theme.typography.pxToRem(15) })}>
                                    Simulate calculation
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ExecuteCalculation pricelistId={calculation.pricelistId} />
                            </AccordionDetails>
                        </Accordion>
                    </Paper>

                    <MarkupCalculator />
                </Grid>
            </Grid>
            <CreateCostDialog />
            <EditCostGroupDialog />
            <EditCostDialog />
            <RecalculatePricesDialog pricelistId={calculation.pricelistId} />
        </div>
    );
}

export default connect(mapStateToProps)(CalculationCanvas);
